import React, { useState, useEffect } from 'react';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Save, 
  Edit, 
  X,
  Home,
  Users,
  Calendar,
  Settings,
  ChevronRight,
  Eye
} from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS, getAuthHeaders } from '../config/api.ts';
import FormSelect from '../components/FormSelect.tsx';

interface WorkingHours {
  open: string;
  close: string;
  is24Hours: boolean;
  isClosed: boolean;
}

interface Branch {
  _id: string;
  name: string;
  code: string;
  address: string;
  phone: string;
  email: string;
  workingHours: {
    monday: WorkingHours;
    tuesday: WorkingHours;
    wednesday: WorkingHours;
    thursday: WorkingHours;
    friday: WorkingHours;
    saturday: WorkingHours;
    sunday: WorkingHours;
  };
  capacity: number;
  services: string[];
  description: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  // Ek alanlar
  manager?: any;
  staffCapacity?: number;
  settings?: {
    autoCheckIn: boolean;
    requireQRCode: boolean;
    notificationEnabled: boolean;
  };
}

const SubeBilgileri: React.FC = () => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [viewingBranch, setViewingBranch] = useState<Branch | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [isEditingInModal, setIsEditingInModal] = useState(false); // Popup içinde düzenleme modu

  const [formData, setFormData] = useState<{
    name: string;
    code: string;
    address: string;
    phone: string;
    email: string;
    workingHours: {
      monday: WorkingHours;
      tuesday: WorkingHours;
      wednesday: WorkingHours;
      thursday: WorkingHours;
      friday: WorkingHours;
      saturday: WorkingHours;
      sunday: WorkingHours;
    };
    capacity: number;
    services: string[];
    description: string;
    isActive: boolean;
  }>({
    name: '',
    code: '',
    address: '',
    phone: '',
    email: '',
    workingHours: {
      monday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
      tuesday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
      wednesday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
      thursday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
      friday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
      saturday: { open: '09:00', close: '16:00', is24Hours: false, isClosed: false },
      sunday: { open: '00:00', close: '00:00', is24Hours: false, isClosed: true }
    },
    capacity: 50,
    services: ['Otel', 'Kreş', 'Grooming', 'Veteriner'],
    description: '',
    isActive: true
  });

  const fetchBranches = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Admin token bulunamadı');
      }

      const headers = getAuthHeaders(token);
      
      // Önce admin bilgilerini al (hangi şubede çalıştığını öğrenmek için)
      const profileResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.STAFF_PROFILE}`, {
        method: 'GET',
        headers,
      });
      
      if (!profileResponse.ok) {
        throw new Error('Admin profil bilgileri alınamadı');
      }
      
      const profileData = await profileResponse.json();
      
      if (!profileData.success || !profileData.staff?.branch?._id) {
        throw new Error('Admin şube bilgisi bulunamadı - Profile: ' + JSON.stringify(profileData));
      }
      
      const adminBranchId = profileData.staff.branch._id;
      
      // Sadece admin'in şubesini getir
      const branchResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.BRANCHES}/${adminBranchId}`, {
        method: 'GET',
        headers,
      });
      
      if (!branchResponse.ok) {
        throw new Error('Şube bilgileri alınamadı');
      }
      
      const branchData = await branchResponse.json();
      
      if (branchData.success && branchData.data) {
        // API'den gelen veriyi frontend formatına dönüştür
        const branch = branchData.data;
        const transformedBranch = {
          _id: branch._id,
          name: branch.name || 'Bilinmeyen Şube',
          code: branch.code || 'N/A',
          address: branch.address ? 
            `${branch.address.street || ''}, ${branch.address.district || ''}, ${branch.address.city || ''}${branch.address.postalCode ? ' ' + branch.address.postalCode : ''}`.replace(/^,\s*|,\s*$/, '').replace(/,\s*,/g, ',') 
            : 'Adres bilgisi yok',
          phone: branch.contact?.phone || 'Telefon bilgisi yok',
          email: branch.contact?.email || 'E-posta bilgisi yok',
          workingHours: branch.workingHours ? {
            monday: branch.workingHours.monday ? {
              open: branch.workingHours.monday.open || '09:00',
              close: branch.workingHours.monday.close || '18:00',
              is24Hours: branch.workingHours.monday.open === '00:00' && branch.workingHours.monday.close === '23:59',
              isClosed: branch.workingHours.monday.open === '00:00' && branch.workingHours.monday.close === '00:00'
            } : { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
            tuesday: branch.workingHours.tuesday ? {
              open: branch.workingHours.tuesday.open || '09:00',
              close: branch.workingHours.tuesday.close || '18:00',
              is24Hours: branch.workingHours.tuesday.open === '00:00' && branch.workingHours.tuesday.close === '23:59',
              isClosed: branch.workingHours.tuesday.open === '00:00' && branch.workingHours.tuesday.close === '00:00'
            } : { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
            wednesday: branch.workingHours.wednesday ? {
              open: branch.workingHours.wednesday.open || '09:00',
              close: branch.workingHours.wednesday.close || '18:00',
              is24Hours: branch.workingHours.wednesday.open === '00:00' && branch.workingHours.wednesday.close === '23:59',
              isClosed: branch.workingHours.wednesday.open === '00:00' && branch.workingHours.wednesday.close === '00:00'
            } : { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
            thursday: branch.workingHours.thursday ? {
              open: branch.workingHours.thursday.open || '09:00',
              close: branch.workingHours.thursday.close || '18:00',
              is24Hours: branch.workingHours.thursday.open === '00:00' && branch.workingHours.thursday.close === '23:59',
              isClosed: branch.workingHours.thursday.open === '00:00' && branch.workingHours.thursday.close === '00:00'
            } : { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
            friday: branch.workingHours.friday ? {
              open: branch.workingHours.friday.open || '09:00',
              close: branch.workingHours.friday.close || '18:00',
              is24Hours: branch.workingHours.friday.open === '00:00' && branch.workingHours.friday.close === '23:59',
              isClosed: branch.workingHours.friday.open === '00:00' && branch.workingHours.friday.close === '00:00'
            } : { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
            saturday: branch.workingHours.saturday ? {
              open: branch.workingHours.saturday.open || '09:00',
              close: branch.workingHours.saturday.close || '16:00',
              is24Hours: branch.workingHours.saturday.open === '00:00' && branch.workingHours.saturday.close === '23:59',
              isClosed: branch.workingHours.saturday.open === '00:00' && branch.workingHours.saturday.close === '00:00'
            } : { open: '09:00', close: '16:00', is24Hours: false, isClosed: false },
            sunday: branch.workingHours.sunday ? {
              open: branch.workingHours.sunday.open || '00:00',
              close: branch.workingHours.sunday.close || '00:00',
              is24Hours: branch.workingHours.sunday.open === '00:00' && branch.workingHours.sunday.close === '23:59',
              isClosed: branch.workingHours.sunday.open === '00:00' && branch.workingHours.sunday.close === '00:00'
            } : { open: '00:00', close: '00:00', is24Hours: false, isClosed: true }
          } : {
            monday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
            tuesday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
            wednesday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
            thursday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
            friday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
            saturday: { open: '09:00', close: '16:00', is24Hours: false, isClosed: false },
            sunday: { open: '00:00', close: '00:00', is24Hours: false, isClosed: true }
          },
          capacity: branch.capacity?.staff || 50,
          services: branch.services ? branch.services.map((service: string) => {
            const serviceMap: { [key: string]: string } = {
              'boarding': 'Otel',
              'daycare': 'Kreş', 
              'grooming': 'Grooming',
              'training': 'Eğitim',
              'veterinary': 'Veteriner'
            };
            return serviceMap[service] || service;
          }) : [],
          description: branch.description || `${branch.name || 'Şube'}. Modern tesislerimizde Kedi/Köpeklerinize en iyi hizmeti sunuyoruz.`,
          isActive: branch.isActive !== undefined ? branch.isActive : true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          // Ek bilgiler - backend'den gelmiyor
          manager: null,
          staffCapacity: 5,
          settings: {
            autoCheckIn: false,
            requireQRCode: true,
            notificationEnabled: true
          }
        };
        
        setBranches([transformedBranch]); // Sadece bir şube
      } else {
        throw new Error(branchData.message || 'Şube bilgileri alınamadı');
      }
    } catch (err) {
      setError('Şube bilgileri getirilemedi: ' + (err instanceof Error ? err.message : 'Bilinmeyen hata'));
      
      // Hata durumunda mock data kullan (test için)
      setBranches([{
        _id: '1',
        name: 'Pawley Club - Bu Admin\'in Şubesi',
        code: 'BC001',
        address: 'Atatürk Mahallesi, Pet Caddesi No:123, İstanbul',
        phone: '+90 212 555 0123',
        email: 'merkez@barkleyclub.com',
        workingHours: {
          monday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
          tuesday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
          wednesday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
          thursday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
          friday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
          saturday: { open: '09:00', close: '16:00', is24Hours: false, isClosed: false },
          sunday: { open: '00:00', close: '00:00', is24Hours: false, isClosed: true }
        },
        capacity: 50,
        services: ['Otel', 'Kreş', 'Grooming', 'Veteriner'],
        description: 'Bu admin\'in yönettiği şube. Modern tesislerimizle Kedi/Köpeklerinize en iyi hizmeti sunuyoruz.',
        isActive: true,
        createdAt: '2024-01-01T00:00:00.000Z',
        updatedAt: '2024-01-01T00:00:00.000Z'
      }]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  const handleViewDetails = (branch: Branch) => {
    setViewingBranch(branch);
    setShowDetails(true);
  };

  const handleEdit = (branch: Branch) => {
    setEditingBranch(branch);
    setFormData({
      name: branch.name || '',
      code: branch.code || '',
      address: branch.address || '',
      phone: branch.phone || '',
      email: branch.email || '',
      workingHours: branch.workingHours || {
        monday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
        tuesday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
        wednesday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
        thursday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
        friday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
        saturday: { open: '09:00', close: '16:00', is24Hours: false, isClosed: false },
        sunday: { open: '00:00', close: '00:00', is24Hours: false, isClosed: true }
      },
      capacity: branch.capacity || 50,
      services: branch.services || [],
      description: branch.description || '',
      isActive: branch.isActive !== undefined ? branch.isActive : true
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingBranch(null);
    setFormData({
      name: '',
      code: '',
      address: '',
      phone: '',
      email: '',
      workingHours: {
        monday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
        tuesday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
        wednesday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
        thursday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
        friday: { open: '09:00', close: '18:00', is24Hours: false, isClosed: false },
        saturday: { open: '09:00', close: '16:00', is24Hours: false, isClosed: false },
        sunday: { open: '00:00', close: '00:00', is24Hours: false, isClosed: true }
      },
      capacity: 50,
      services: ['Otel', 'Kreş', 'Grooming', 'Veteriner'],
      description: '',
      isActive: true
    });
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Admin token bulunamadı');
      }

      const headers = getAuthHeaders(token);
      
      if (!editingBranch) {
        throw new Error('Düzenlenecek şube bulunamadı');
      }
      
      // Backend formatına uygun veri hazırla (sadece çalışma saatleri)
      // is24Hours ve isClosed durumlarını open/close değerlerine göre ayarla
      const formatWorkingHours = (dayData: WorkingHours) => {
        if (dayData.is24Hours) {
          return { open: '00:00', close: '23:59' };
        } else if (dayData.isClosed) {
          return { open: '00:00', close: '00:00' };
        } else {
          return { open: dayData.open, close: dayData.close };
        }
      };

      const updateData = {
        workingHours: {
          monday: formatWorkingHours(formData.workingHours.monday),
          tuesday: formatWorkingHours(formData.workingHours.tuesday),
          wednesday: formatWorkingHours(formData.workingHours.wednesday),
          thursday: formatWorkingHours(formData.workingHours.thursday),
          friday: formatWorkingHours(formData.workingHours.friday),
          saturday: formatWorkingHours(formData.workingHours.saturday),
          sunday: formatWorkingHours(formData.workingHours.sunday)
        }
      };
      
      // Şube güncelleme API çağrısı (PUT endpoint)
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.BRANCHES_UPDATE.replace('{id}', editingBranch._id)}`, {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Başarılı güncelleme - verileri yeniden çek
        await fetchBranches();
        setSuccess(data.message || 'Şube bilgileri başarıyla güncellendi');
        setIsEditing(false);
        setEditingBranch(null);
      } else {
        throw new Error(data.message || `Güncelleme başarısız: ${response.status}`);
      }
    } catch (err) {
      setError('Şube bilgileri güncellenemedi: ' + (err instanceof Error ? err.message : 'Bilinmeyen hata'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    if (field.startsWith('workingHours.')) {
      const day = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        workingHours: {
          ...(prev.workingHours || {}),
          [day]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Custom saat seçici component'i
  const TimeSelector = ({ value, onChange, label }: { value: string; onChange: (value: string) => void; label: string }) => {
    const [hours, minutes] = value.split(':');
    
    const handleHoursChange = (newHours: string) => {
      const paddedHours = newHours.padStart(2, '0');
      onChange(`${paddedHours}:${minutes}`);
    };
    
    const handleMinutesChange = (newMinutes: string) => {
      const paddedMinutes = newMinutes.padStart(2, '0');
      onChange(`${hours}:${paddedMinutes}`);
    };

    return (
      <div className="flex-1">
        <label className="block text-xs text-gray-600 mb-1">{label}</label>
        <div className="flex items-center space-x-2">
          <FormSelect
            value={hours}
            onChange={(e) => handleHoursChange(e.target.value)}
            containerClassName="w-20 space-y-0"
            className="text-center font-mono text-sm py-1.5 px-2"
            labelClassName=""
          >
            {Array.from({ length: 24 }, (_, i) => (
              <option key={i} value={i.toString().padStart(2, '0')}>
                {i.toString().padStart(2, '0')}
              </option>
            ))}
          </FormSelect>
          <span className="text-gray-500 font-mono text-sm">:</span>
          <FormSelect
            value={minutes}
            onChange={(e) => handleMinutesChange(e.target.value)}
            containerClassName="w-20 space-y-0"
            className="text-center font-mono text-sm py-1.5 px-2"
            labelClassName=""
          >
            {Array.from({ length: 60 }, (_, i) => (
              <option key={i} value={i.toString().padStart(2, '0')}>
                {i.toString().padStart(2, '0')}
              </option>
            ))}
          </FormSelect>
        </div>
      </div>
    );
  };

  const handleWorkingHoursChange = (day: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      workingHours: {
        ...(prev.workingHours || {}),
        [day]: {
          ...(prev.workingHours?.[day as keyof typeof prev.workingHours] || {}),
          [field]: value,
          // Eğer 24 saat açık seçilirse, saatleri ayarla
          ...(field === 'is24Hours' && value ? {
            open: '00:00',
            close: '23:59',
            isClosed: false
          } : {}),
          // Eğer kapalı seçilirse, saatleri sıfırla
          ...(field === 'isClosed' && value ? {
            open: '00:00',
            close: '00:00',
            is24Hours: false
          } : {}),
          // Eğer normal saat seçilirse, 24 saat ve kapalı durumlarını sıfırla
          ...(field === 'open' || field === 'close' ? {
            is24Hours: false,
            isClosed: false
          } : {})
        }
      }
    }));
  };

  const handleServiceToggle = (service: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services && prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...(prev.services || []), service]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Şube bilgileri yükleniyor...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Şube Bilgileri</h1>
          <p className="text-gray-600">Şubenizdeki bilgileri görüntüleyin ve düzenleyin</p>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* Branch Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {isEditing ? (
          /* Edit Form */
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-medium text-gray-800">Çalışma Saatlerini Düzenle</h2>
              <div className="flex space-x-2">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center text-sm"
                >
                  <X size={16} className="mr-2" />
                  İptal
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center disabled:opacity-50 text-sm"
                >
                  <Save size={16} className="mr-2" />
                  {loading ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </div>

            <div className="max-w-4xl mx-auto">
              {/* Working Hours */}
              <div className="space-y-3">
                <h3 className="text-base font-medium text-gray-800 mb-4">Çalışma Saatleri</h3>
                
                {formData.workingHours && Object.entries(formData.workingHours).map(([day, dayData]) => (
                  <div key={day} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">
                        {day === 'monday' ? 'Pazartesi' :
                         day === 'tuesday' ? 'Salı' :
                         day === 'wednesday' ? 'Çarşamba' :
                         day === 'thursday' ? 'Perşembe' :
                         day === 'friday' ? 'Cuma' :
                         day === 'saturday' ? 'Cumartesi' :
                         'Pazar'}
                      </label>
                      <div className="flex items-center space-x-3">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={dayData.is24Hours}
                            onChange={(e) => handleWorkingHoursChange(day as keyof typeof formData.workingHours, 'is24Hours', e.target.checked)}
                            className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-xs text-gray-600">24 Saat Açık</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={dayData.isClosed}
                            onChange={(e) => handleWorkingHoursChange(day as keyof typeof formData.workingHours, 'isClosed', e.target.checked)}
                            className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-xs text-gray-600">Kapalı</span>
                        </label>
                      </div>
                    </div>
                    
                    {!dayData.is24Hours && !dayData.isClosed && (
                      <div className="flex items-center space-x-3 mt-2">
                        <TimeSelector
                          value={dayData.open}
                          onChange={(value) => handleWorkingHoursChange(day as keyof typeof formData.workingHours, 'open', value)}
                          label="Açılış Saati"
                        />
                        <TimeSelector
                          value={dayData.close}
                          onChange={(value) => handleWorkingHoursChange(day as keyof typeof formData.workingHours, 'close', value)}
                          label="Kapanış Saati"
                        />
                      </div>
                    )}
                    
                    {(dayData.is24Hours || dayData.isClosed) && (
                      <div className="text-xs text-gray-500 italic mt-1">
                        {dayData.is24Hours ? '24 saat açık' : 'Kapalı'}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* View Mode */
          <div className="p-6">
            {branches.length === 0 ? (
              <div className="text-center py-12">
                <Home size={40} className="mx-auto text-gray-400 mb-3" />
                <h3 className="text-base font-medium text-gray-800 mb-1">Şube Bilgisi Bulunamadı</h3>
                <p className="text-sm text-gray-600">Hesabınızla ilişkili şube bilgisi bulunamadı.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6">
                {branches.map((branch) => (
                  <div key={branch._id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                    {/* Card Header */}
                    <div className="p-6 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-gray-100 rounded-lg">
                            <Home size={20} className="text-gray-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-800">{branch.name}</h3>
                            <p className="text-sm text-gray-500">Kod: {branch.code}</p>
                          </div>
                        </div>
                        <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${
                          branch.isActive 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {branch.isActive ? 'Aktif' : 'Pasif'}
                        </span>
                      </div>
                    </div>

                    {/* Card Content */}
                    <div className="p-6 space-y-4">
                      {/* Basic Info */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-3">
                          <div className="flex items-start space-x-3">
                            <MapPin size={18} className="text-blue-500 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-700 mb-1">Adres</p>
                              <p className="text-sm text-gray-600 leading-relaxed">{branch.address}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-start space-x-3">
                            <Phone size={18} className="text-green-500 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-gray-700 mb-1">Telefon</p>
                              <p className="text-sm text-gray-600">{branch.phone}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex items-start space-x-3">
                            <Mail size={18} className="text-purple-500 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-gray-700 mb-1">E-posta</p>
                              <p className="text-sm text-gray-600">{branch.email}</p>
                            </div>
                          </div>
                          
                        </div>
                      </div>

                      {/* Working Hours Preview */}
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-start space-x-3 flex-1">
                            <Clock size={18} className="text-indigo-500 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-700">Çalışma Saatleri</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleEdit(branch)}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors flex items-center"
                          >
                            <Edit size={14} className="mr-1.5" />
                            Güncelle
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {branch.workingHours && Object.entries(branch.workingHours).map(([day, dayData]) => (
                            <div key={day} className="flex justify-between items-center py-0.5">
                              <span className="text-xs font-medium text-gray-700">
                                {day === 'monday' ? 'Pazartesi' :
                                 day === 'tuesday' ? 'Salı' :
                                 day === 'wednesday' ? 'Çarşamba' :
                                 day === 'thursday' ? 'Perşembe' :
                                 day === 'friday' ? 'Cuma' :
                                 day === 'saturday' ? 'Cumartesi' :
                                 'Pazar'}:
                              </span>
                              <span className="text-xs text-gray-600">
                                {dayData.is24Hours ? '24 Saat Açık' :
                                 dayData.isClosed ? 'Kapalı' :
                                 `${dayData.open} - ${dayData.close}`}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Services Preview */}
                      {branch.services && branch.services.length > 0 && (
                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                          <div className="flex items-start space-x-3">
                            <Settings size={18} className="text-blue-500 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-700 mb-2">Hizmetler</p>
                              <div className="flex flex-wrap gap-2">
                                {branch.services.slice(0, 3).map((service) => (
                                  <span
                                    key={service}
                                    className="inline-flex px-2.5 py-1 text-xs font-medium bg-blue-500 text-white rounded-md"
                                  >
                                    {service}
                                  </span>
                                ))}
                                {branch.services.length > 3 && (
                                  <span className="inline-flex px-2.5 py-1 text-xs font-medium bg-gray-500 text-white rounded-md">
                                    +{branch.services.length - 3} daha
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Card Footer */}
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 rounded-b-lg">
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-500">
                          <span className="flex items-center">
                            <Calendar size={14} className="mr-1.5" />
                            Son güncelleme: {new Date(branch.updatedAt).toLocaleDateString('tr-TR')}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleViewDetails(branch)}
                            className="flex items-center space-x-2 px-3 py-1.5 text-xs font-medium text-white bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors"
                          >
                            <Eye size={14} />
                            <span>Detayları Görüntüle</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Branch Details Modal */}
      {showDetails && viewingBranch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Home size={20} className="text-gray-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">{viewingBranch.name}</h2>
                  <p className="text-sm text-gray-500">Şube Kodu: {viewingBranch.code}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${
                  viewingBranch.isActive 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {viewingBranch.isActive ? 'Aktif' : 'Pasif'}
                </span>
                <button
                  onClick={() => setShowDetails(false)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-5 space-y-6">
              {isEditingInModal ? (
                /* Düzenleme Formu - Popup İçinde */
                <div>
                  <div className="mb-4">
                    <h3 className="text-lg font-medium text-gray-800 mb-2">Çalışma Saatlerini Düzenle</h3>
                    <p className="text-sm text-gray-600">Şube: {viewingBranch.name}</p>
                  </div>

                  {/* Working Hours Edit Form */}
                  <div className="space-y-3">
                    {viewingBranch.workingHours && Object.entries(viewingBranch.workingHours).map(([day, dayData]) => {
                      const dayKey = day as keyof typeof viewingBranch.workingHours;
                      return (
                        <div key={day} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-gray-700">
                              {day === 'monday' ? 'Pazartesi' :
                               day === 'tuesday' ? 'Salı' :
                               day === 'wednesday' ? 'Çarşamba' :
                               day === 'thursday' ? 'Perşembe' :
                               day === 'friday' ? 'Cuma' :
                               day === 'saturday' ? 'Cumartesi' :
                               'Pazar'}
                            </label>
                            <div className="flex items-center space-x-3">
                              <label className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={dayData.is24Hours}
                                  onChange={(e) => {
                                    const updatedBranch = { ...viewingBranch };
                                    updatedBranch.workingHours[dayKey] = {
                                      ...dayData,
                                      is24Hours: e.target.checked,
                                      isClosed: e.target.checked ? false : dayData.isClosed
                                    };
                                    setViewingBranch(updatedBranch);
                                  }}
                                  className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300 rounded"
                                />
                                <span className="ml-2 text-xs text-gray-600">24 Saat Açık</span>
                              </label>
                              <label className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={dayData.isClosed}
                                  onChange={(e) => {
                                    const updatedBranch = { ...viewingBranch };
                                    updatedBranch.workingHours[dayKey] = {
                                      ...dayData,
                                      isClosed: e.target.checked,
                                      is24Hours: e.target.checked ? false : dayData.is24Hours
                                    };
                                    setViewingBranch(updatedBranch);
                                  }}
                                  className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300 rounded"
                                />
                                <span className="ml-2 text-xs text-gray-600">Kapalı</span>
                              </label>
                            </div>
                          </div>
                          
                          {!dayData.is24Hours && !dayData.isClosed && (
                            <div className="flex items-center space-x-3 mt-2">
                              <div className="flex-1">
                                <label className="block text-xs text-gray-600 mb-1">Açılış Saati</label>
                                <input
                                  type="time"
                                  value={dayData.open}
                                  onChange={(e) => {
                                    const updatedBranch = { ...viewingBranch };
                                    updatedBranch.workingHours[dayKey] = {
                                      ...dayData,
                                      open: e.target.value
                                    };
                                    setViewingBranch(updatedBranch);
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div className="flex-1">
                                <label className="block text-xs text-gray-600 mb-1">Kapanış Saati</label>
                                <input
                                  type="time"
                                  value={dayData.close}
                                  onChange={(e) => {
                                    const updatedBranch = { ...viewingBranch };
                                    updatedBranch.workingHours[dayKey] = {
                                      ...dayData,
                                      close: e.target.value
                                    };
                                    setViewingBranch(updatedBranch);
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                            </div>
                          )}
                          
                          {(dayData.is24Hours || dayData.isClosed) && (
                            <div className="text-xs text-gray-500 italic mt-1">
                              {dayData.is24Hours ? '24 saat açık' : 'Kapalı'}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <>
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-800 flex items-center">
                    <Home size={16} className="mr-2" />
                    Temel Bilgiler
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <MapPin size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-xs font-medium text-blue-900 mb-1">Adres Bilgisi</p>
                          <p className="text-xs text-gray-700 leading-relaxed">{viewingBranch.address}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-2">
                      <Phone size={14} className="text-green-500 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-gray-700">Telefon</p>
                        <p className="text-xs text-gray-600">{viewingBranch.phone}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-2">
                      <Mail size={14} className="text-purple-500 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-gray-700">E-posta</p>
                        <p className="text-xs text-gray-600">{viewingBranch.email}</p>
                      </div>
                    </div>
                    
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-800 flex items-center justify-between">
                    <div className="flex items-center">
                      <Clock size={16} className="mr-2" />
                      Çalışma Saatleri
                    </div>
                    <button
                      onClick={() => {
                        setEditingBranch(viewingBranch);
                        setIsEditing(true);
                        setShowDetails(false);
                      }}
                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                      title="Çalışma saatlerini düzenle"
                    >
                      <Edit size={14} />
                    </button>
                  </h3>
                  
                  <div className="space-y-1.5">
                    {viewingBranch.workingHours && Object.entries(viewingBranch.workingHours).map(([day, dayData]) => (
                      <div key={day} className="flex justify-between items-center">
                        <span className="text-xs font-medium text-gray-700">
                          {day === 'monday' ? 'Pazartesi' :
                           day === 'tuesday' ? 'Salı' :
                           day === 'wednesday' ? 'Çarşamba' :
                           day === 'thursday' ? 'Perşembe' :
                           day === 'friday' ? 'Cuma' :
                           day === 'saturday' ? 'Cumartesi' :
                           'Pazar'}
                        </span>
                        <span className="text-xs text-gray-600">
                          {dayData.is24Hours ? '24 Saat Açık' :
                           dayData.isClosed ? 'Kapalı' :
                           `${dayData.open} - ${dayData.close}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Services */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-800 flex items-center">
                  <Settings size={16} className="mr-2" />
                  Hizmetler
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {viewingBranch.services && viewingBranch.services.map((service) => (
                    <span
                      key={service}
                      className="inline-flex px-2.5 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full"
                    >
                      {service}
                    </span>
                  ))}
                </div>
              </div>

              {/* Description */}
              {viewingBranch.description && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-gray-800">Açıklama</h3>
                  <p className="text-xs text-gray-600 leading-relaxed">{viewingBranch.description}</p>
                </div>
              )}

              {/* Meta Information */}
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center space-x-3">
                    <span className="flex items-center">
                      <Calendar size={12} className="mr-1" />
                      Oluşturulma: {new Date(viewingBranch.createdAt).toLocaleDateString('tr-TR')}
                    </span>
                    <span className="flex items-center">
                      <Calendar size={12} className="mr-1" />
                      Güncelleme: {new Date(viewingBranch.updatedAt).toLocaleDateString('tr-TR')}
                    </span>
                  </div>
                </div>
              </div>
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
              {isEditingInModal ? (
                <>
                  <button
                    onClick={() => {
                      setIsEditingInModal(false);
                      // viewingBranch'i eski haline geri yükle
                      const originalBranch = branches.find(b => b._id === viewingBranch?._id);
                      if (originalBranch) {
                        setViewingBranch(originalBranch);
                      }
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                  >
                    <X size={16} className="mr-2" />
                    İptal
                  </button>
                  <button
                    onClick={async () => {
                      if (viewingBranch) {
                        setEditingBranch(viewingBranch);
                        setFormData({
                          name: viewingBranch.name || '',
                          code: viewingBranch.code || '',
                          address: viewingBranch.address || '',
                          phone: viewingBranch.phone || '',
                          email: viewingBranch.email || '',
                          workingHours: viewingBranch.workingHours,
                          capacity: viewingBranch.capacity || 50,
                          services: viewingBranch.services || [],
                          description: viewingBranch.description || '',
                          isActive: viewingBranch.isActive !== undefined ? viewingBranch.isActive : true
                        });
                        await handleSave();
                        setIsEditingInModal(false);
                        setShowDetails(false);
                        fetchBranches(); // Verileri yenile
                      }
                    }}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50"
                  >
                    <Save size={16} className="mr-2" />
                    {loading ? 'Kaydediliyor...' : 'Kaydet'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setIsEditingInModal(true);
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                  >
                    <Edit size={16} className="mr-2" />
                    Çalışma Saatlerini Düzenle
                  </button>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Kapat
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubeBilgileri;


